import { type SupportedLanguage, SupportedLanguages } from '@centric-os/platform/i18n';
import { useAuthStore, useSplitIoStore } from '@centric-os/stores';
import { FeatureFlags, P2Role, PermissionScope } from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';

interface State extends StateTree {
  reportURL: string;
}

export enum LookerLocales {
  FRENCH = 'fr_CA',
  ENGLISH = 'en',
}

export type LookerLocale = `${LookerLocales}`;

// https://cloud.google.com/looker/docs/supported-user-interface-languages
export const LOOKER_LOCALE_RECORD: Record<SupportedLanguage, LookerLocale> = {
  [SupportedLanguages.FRENCH]: LookerLocales.FRENCH,
  [SupportedLanguages.ENGLISH]: LookerLocales.ENGLISH,
};

export function useReportStore(storeId = 'reports') {
  const store = defineStore(storeId, {
    state: (): State => ({
      reportURL: null,
    }),
    actions: {
      async postLookerEmbedUrl(url: string, locale: SupportedLanguage) {
        const request = await this.cdlApi.post('/user/looker/embedurl', {
          url,
          locale: LOOKER_LOCALE_RECORD[locale],
        });

        this.reportURL = request.data.embed_url;
      },
    },
    getters: {
      canAccessReportsSection(): boolean {
        const authStore = useAuthStore();
        const { isFeatureOn } = useSplitIoStore();
        // Legacy access: requires feature flag AND (legacy permission OR admin role)
        const canUseLegacyReportsPerm =
          (authStore.hasPermissions([PermissionScope.legacyReportsPermission]) ||
            authStore.hasRole(P2Role.ADMIN)) &&
          isFeatureOn(FeatureFlags.USE_LEGACY_REPORT_PERM);
        return authStore.hasPermissions([PermissionScope.reports]) || canUseLegacyReportsPerm;
      },
    },
    pagination: false,
  });

  return store();
}
